import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { categoriesAPI } from '../services/api';
import { ArrowRight } from 'lucide-react';

const Categories = () => {
  const { t, currentLanguage } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const categoriesData = await categoriesAPI.getAll();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const getCategoryName = (category) => {
    return category.name[currentLanguage] || category.name.en || category.name;
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-capsule-bg-light">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('shop_categories')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white/50 rounded-3xl h-36 md:h-48"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-capsule-bg-light">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-capsule-coral">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16 bg-capsule-bg-light relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-capsule-teal/10 to-capsule-aqua/5 blur-2xl"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-gradient-to-br from-capsule-coral/10 to-capsule-salmon/5 blur-2xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block text-capsule-teal text-sm font-semibold mb-2">
            {currentLanguage === 'es' ? 'EXPLORAR' : currentLanguage === 'ru' ? 'ИССЛЕДОВАТЬ' : 'EXPLORE'}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {t('shop_categories')}
          </h2>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {categories.map((category, index) => (
            <Link key={category.id} to={`/products?category=${category.id}`}>
              <div className="group cursor-pointer glass-card rounded-3xl overflow-hidden hover:shadow-glass-hover transition-all duration-500 hover:-translate-y-2">
                <div className="relative overflow-hidden">
                  <img
                    src={category.image_url || '/placeholder-category.png'}
                    alt={getCategoryName(category)}
                    className="w-full h-36 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-category.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-sm md:text-lg group-hover:text-capsule-aqua transition-colors line-clamp-2">
                      {getCategoryName(category)}
                    </h3>
                    <div className="flex items-center gap-1 mt-2 text-capsule-aqua text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>{currentLanguage === 'es' ? 'Ver productos' : currentLanguage === 'ru' ? 'Смотреть' : 'View products'}</span>
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Categories;
